import '@react-pdf-viewer/search/lib/styles/index.css';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import HighlightIcon from '@mui/icons-material/Highlight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CharacterMap,
  SetRenderRange,
  SpecialZoomLevel,
  Viewer,
  VisiblePagesRange,
  Worker,
} from '@react-pdf-viewer/core';
import { searchPlugin } from '@react-pdf-viewer/search';
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  useFetchList,
  useUpdateItem,
} from 'services/common/react-query-common-service';
import { ReplaceData } from 'services/CommonServices';
import { sseService } from 'services/SseService';
import { MASTER_ENDPOINT, SALES_ENDPOINT } from 'utils/Constant';

import ShortlistModal from '../../view-candidate/components/ShortlistModal';

// ── JD Keyword Extraction ──────────────────────────────────────────────────

const extractJDKeywords = (jobInfo: any, booleanJsonData: any): string[] => {
  const keywords = new Set<string>();
  const STOP_WORDS = new Set([
    'AND',
    'OR',
    'NOT',
    'THE',
    'WITH',
    'FOR',
    'IN',
    'OF',
    'TO',
    'AT',
    'BY',
    'ON',
    'A',
    'AN',
    'IS',
    'AS',
    'BE',
  ]);

  const addWord = (word: string) => {
    const cleaned = word.replace(/[()"',;:.!?]/g, '').trim();
    if (cleaned.length > 2 && !STOP_WORDS.has(cleaned.toUpperCase())) {
      keywords.add(cleaned);
    }
  };

  // Parse boolean Q string — extract quoted phrases & meaningful tokens
  const qStr: string =
    booleanJsonData?.payload?.searchParameters?.q ??
    booleanJsonData?.searchParameters?.q ??
    booleanJsonData?.q ??
    '';

  if (qStr) {
    // Extract quoted multi-word phrases first
    const phraseRegex = /"([^"]+)"/g;
    let phraseMatch;
    while ((phraseMatch = phraseRegex.exec(qStr)) !== null) {
      const phrase = phraseMatch[1].trim();
      if (phrase.length > 2) keywords.add(phrase);
    }
    // Extract remaining individual tokens
    qStr
      .replace(/"[^"]+"/g, ' ')
      .split(/\s+/)
      .forEach(addWord);
  }

  return Array.from(keywords).filter(Boolean).slice(0, 40);
};

// ── JD Highlight PDF Viewer ────────────────────────────────────────────────

const JDHighlightPDFViewer: React.FC<{
  fileUrl: string;
  jobInfo: any;
  booleanJsonData: any;
}> = ({ fileUrl, jobInfo, booleanJsonData }) => {
  const [highlightsEnabled, setHighlightsEnabled] = React.useState(true);
  const [matchCount, setMatchCount] = React.useState(0);
  const [isDocLoaded, setIsDocLoaded] = React.useState(false);

  const keywords = React.useMemo(
    () => extractJDKeywords(jobInfo, booleanJsonData),
    [jobInfo, booleanJsonData],
  );

  const highlightsEnabledRef = React.useRef(highlightsEnabled);
  highlightsEnabledRef.current = highlightsEnabled;

  const searchPluginConfig = React.useMemo(
    () => ({
      onHighlightKeyword: (props: any) => {
        if (highlightsEnabledRef.current) {
          props.highlightEle.style.backgroundColor = 'rgba(255, 220, 0, 0.55)';
          props.highlightEle.style.borderRadius = '2px';
        } else {
          props.highlightEle.style.backgroundColor = 'transparent';
        }
      },
    }),
    [],
  );

  const searchPluginInstance = searchPlugin(searchPluginConfig);

  const searchPluginInstanceRef = React.useRef(searchPluginInstance);
  searchPluginInstanceRef.current = searchPluginInstance;

  const runHighlights = React.useCallback(async () => {
    if (!isDocLoaded || keywords.length === 0) return;
    try {
      const plugin = searchPluginInstanceRef.current;
      if (!highlightsEnabled) {
        plugin.clearHighlights();
        setMatchCount(0);
        return;
      }
      const allMatches = await plugin.highlight(
        keywords.map((kw) => ({ keyword: kw, matchCase: false })),
      );
      setMatchCount(allMatches?.length ?? 0);
    } catch (_) {
      // ignore
    }
  }, [isDocLoaded, keywords, highlightsEnabled]);

  React.useEffect(() => {
    if (isDocLoaded) {
      void runHighlights();
    }
  }, [isDocLoaded, highlightsEnabled, runHighlights]);

  const setRenderRange: SetRenderRange = React.useCallback(
    (visiblePagesRange: VisiblePagesRange) => ({
      endPage:
        visiblePagesRange.startPage <= 20
          ? Math.max(20, visiblePagesRange.endPage + 5)
          : visiblePagesRange.endPage + 5,
      startPage:
        visiblePagesRange.endPage <= 20 ? 0 : visiblePagesRange.startPage - 5,
    }),
    [],
  );

  const characterMap: CharacterMap = {
    isCompressed: true,
    url: 'https://unpkg.com/pdfjs-dist@2.6.347/cmaps/',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Highlight toggle bar */}
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          flexShrink: 0,
          gap: 1.5,
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <HighlightIcon
            sx={{
              color: highlightsEnabled ? '#d97706' : '#9ca3af',
              fontSize: 18,
            }}
          />
          <Typography
            sx={{ color: '#374151', fontSize: '13px', fontWeight: 600 }}>
            Search Query Match
          </Typography>
          {isDocLoaded && keywords.length > 0 && (
            <Chip
              label={
                highlightsEnabled
                  ? `${matchCount} match${matchCount !== 1 ? 'es' : ''}`
                  : `${keywords.length} keyword${
                      keywords.length !== 1 ? 's' : ''
                    }`
              }
              size="small"
              sx={{
                backgroundColor: highlightsEnabled ? '#fef3c7' : '#f3f4f6',
                color: highlightsEnabled ? '#92400e' : '#6b7280',
                fontSize: '11px',
                fontWeight: 600,
                height: 20,
              }}
            />
          )}
          {!isDocLoaded && (
            <Typography sx={{ color: '#9ca3af', fontSize: '11px' }}>
              Loading PDF…
            </Typography>
          )}
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
          <Typography
            sx={{
              color: highlightsEnabled ? '#d97706' : '#9ca3af',
              fontSize: '12px',
              fontWeight: 500,
            }}>
            Highlight Matching Skills
          </Typography>
          <Switch
            checked={highlightsEnabled}
            onChange={(e) => setHighlightsEnabled(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#d97706',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#d97706',
              },
            }}
          />
        </Box>
      </Box>

      {/* PDF Viewer */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
          <Viewer
            fileUrl={fileUrl}
            defaultScale={SpecialZoomLevel.PageWidth}
            plugins={[searchPluginInstance]}
            characterMap={characterMap}
            setRenderRange={setRenderRange}
            enableSmoothScroll={false}
            onDocumentLoad={() => setIsDocLoaded(true)}
          />
        </Worker>
      </Box>
    </Box>
  );
};

interface JobBoardSearchProps {
  jobId?: string;
  jobInfo?: any;
  phase: 'idle' | 'boolean' | 'loading' | 'results';
  results: any[];
  booleanJsonData: any;
  originalBooleanJsonData: any;
  onPhaseChange: (phase: 'idle' | 'boolean' | 'loading' | 'results') => void;
  onResultsChange: (results: any[]) => void;
  onBooleanJsonDataChange: (data: any) => void;
  onOriginalBooleanJsonDataChange: (data: any) => void;
  onOpenCandidateModal?: (candidateId?: string) => void;
  score?: string;
  isScoreModified?: boolean;
  /** Called when the full modal should close */
  onClose?: () => void;
  localScore?: string;
  onScoreChange?: (score: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const activeDate = new Date(dateStr);
    if (isNaN(activeDate.getTime())) return '';
    const currentDate = new Date();
    const diffTime = currentDate.getTime() - activeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  } catch (e) {
    return '';
  }
};

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const extractMatchInfo = (cand: any) => {
  if (!cand) return null;

  const jobMatchCandidateId =
    cand.matchedCandidate?.id ||
    cand.matchedCandidates?.[0]?.id ||
    cand.jobMatchCandidateId ||
    null;

  const jobOverviewFileId =
    cand.jobOverviewFileId ||
    cand.matchedCandidate?.jobOverviewFileId ||
    cand.matchedCandidates?.[0]?.jobOverviewFileId ||
    null;

  const jobOverviewWithoutGapsFileId =
    cand.jobOverviewWithoutGapsFileId ||
    cand.matchedCandidate?.jobOverviewWithoutGapsFileId ||
    cand.matchedCandidates?.[0]?.jobOverviewWithoutGapsFileId ||
    null;

  return {
    jobMatchCandidateId,
    jobOverviewFileId,
    jobOverviewWithoutGapsFileId,
  };
};

// ── Payload helpers ────────────────────────────────────────────────────────

interface EditableItem {
  keyPath: string[];
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'boolean'
    | 'locationArray'
    | 'multiselect';
  value: any;
  isEditable?: boolean;
}

const setDeepValue = (obj: any, path: string[], value: any) => {
  const newObj = JSON.parse(JSON.stringify(obj));
  let current = newObj;
  for (let i = 0; i < path.length - 1; i++) {
    const part = path[i];
    if (!current[part]) current[part] = {};
    current = current[part];
  }
  current[path[path.length - 1]] = value;
  return newObj;
};

const formatLocationValue = (val: any) => {
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (typeof item === 'object' && item !== null)
          return item.value || JSON.stringify(item);
        return String(item);
      })
      .join(', ');
  }
  return String(val ?? '');
};

const parseLocationValue = (str: string, originalVal: any) => {
  if (Array.isArray(originalVal)) {
    return str
      .split(/,|\|/)
      .map((item) => ({ value: item.trim() }))
      .filter((item) => item.value);
  }
  return str;
};

const options = [
  '-',
  'us citizenship',
  'green card',
  'employment auth document',
  'have h1',
  'need h1',
  'canadian citizen',
  'tn permit holder',
];

const getSelectedArray = (val: any): string[] => {
  if (!val || val === '-') return ['-'];
  return String(val)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => options.includes(s));
};

const getEditableFields = (data: any): EditableItem[] => {
  if (!data) return [];
  const items: EditableItem[] = [];

  let spParentPath: string[] = [];
  let spObj: any = null;

  if (data.payload?.searchParameters) {
    spParentPath = ['payload', 'searchParameters'];
    spObj = data.payload.searchParameters;
  } else if (data.searchParameters) {
    spParentPath = ['searchParameters'];
    spObj = data.searchParameters;
  } else {
    spParentPath = [];
    spObj = data;
  }

  if (spObj && typeof spObj === 'object') {
    Object.entries(spObj).forEach(([key, val]) => {
      if (
        ['excludefounders', 'page', 'pagesize', 'lastactive', 'input'].includes(
          key.toLowerCase(),
        )
      )
        return;

      if (key === 'q') {
        items.push({
          keyPath: [...spParentPath, key],
          label: 'Q',
          type: 'textarea',
          value: val,
        });
      } else if (key === 'jobTitleKeyword') {
        items.push({
          keyPath: [...spParentPath, key],
          label: 'JOB TITLE KEYWORD',
          type: 'textarea',
          value: val,
        });
      } else if (key === 'locations') {
        items.push({
          keyPath: [...spParentPath, key],
          label: 'LOCATIONS',
          type: 'locationArray',
          value: val,
        });
      } else if (key === 'workPermit') {
        items.push({
          keyPath: [...spParentPath, key],
          label: ' WORK AUTH',
          type: 'multiselect',
          value: val,
        });
      } else {
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (s) => s.toUpperCase())
          .toUpperCase();
        items.push({
          isEditable: key.toLowerCase() !== 'input',
          keyPath: [...spParentPath, key],
          label,
          type:
            typeof val === 'boolean'
              ? 'boolean'
              : typeof val === 'number'
              ? 'number'
              : 'text',
          value: val,
        });
      }
    });

    const hasWorkPermit = Object.keys(spObj).includes('workPermit');
    if (!hasWorkPermit) {
      items.push({
        keyPath: [...spParentPath, 'workPermit'],
        label: 'WORK AUTH',
        type: 'multiselect',
        value: '',
      });
    }
  }
  return items;
};

// ── Editable field row ─────────────────────────────────────────────────────

const BooleanParameterRow: React.FC<{
  item: EditableItem;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: (val: any) => void;
  disabled?: boolean;
}> = ({ item, isEditing, onStartEdit, onCancel, onSave, disabled }) => {
  const [tempValue, setTempValue] = useState<any>(item.value);

  React.useEffect(() => {
    setTempValue(item.value);
  }, [item.value]);

  const handleSelectChange = (event: any) => {
    const value = event.target.value;
    let newSelected: string[] =
      typeof value === 'string' ? value.split(',') : value;

    if (newSelected.includes('-')) {
      const hadDash = getSelectedArray(tempValue).includes('-');
      if (!hadDash) {
        newSelected = ['-'];
      } else {
        newSelected = newSelected.filter((v) => v !== '-');
      }
    }

    if (newSelected.length === 0) {
      newSelected = ['-'];
    }

    if (newSelected.includes('-')) {
      setTempValue('-');
    } else {
      setTempValue(newSelected.join(', '));
    }
  };

  const handleSave = () => {
    if (item.type === 'locationArray') {
      onSave(parseLocationValue(tempValue, item.value));
    } else if (item.type === 'number') {
      onSave(Number(tempValue));
    } else {
      onSave(tempValue);
    }
  };

  const displayValue =
    item.type === 'locationArray'
      ? formatLocationValue(item.value)
      : String(item.value ?? '');

  if (isEditing) {
    return (
      <Box
        sx={{
          backgroundColor: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderRadius: '8px',
          mb: 1.5,
          p: 2,
        }}>
        <Typography
          sx={{
            color: '#475569',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            mb: 1,
            textTransform: 'uppercase',
          }}>
          {item.label}
          {(item.label === 'Q' || item.label === 'JOB TITLE KEYWORD') && (
            <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>
          )}
        </Typography>
        {item.type === 'textarea' ? (
          <TextField
            multiline
            rows={item.label === 'Q' ? 6 : 3}
            fullWidth
            size="small"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
              },
            }}
          />
        ) : item.type === 'boolean' ? (
          <TextField
            select
            fullWidth
            size="small"
            value={String(tempValue)}
            SelectProps={{
              native: true,
            }}
            onChange={(e) => setTempValue(e.target.value === 'true')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
              },
            }}>
            <option value="true">True</option>
            <option value="false">False</option>
          </TextField>
        ) : item.type === 'multiselect' ? (
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={getSelectedArray(tempValue)}
              onChange={handleSelectChange}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              sx={{
                backgroundColor: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
              }}>
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  <Checkbox
                    checked={getSelectedArray(tempValue).includes(opt)}
                  />
                  <Typography sx={{ fontSize: '13px' }}>{opt}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            value={
              item.type === 'locationArray' && typeof tempValue !== 'string'
                ? formatLocationValue(tempValue)
                : tempValue
            }
            onChange={(e) => setTempValue(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
              },
            }}
          />
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            sx={{
              '&:hover': { backgroundColor: '#3730a3' },
              backgroundColor: '#4f46e5',
              boxShadow: 'none',
              color: '#fff',
              fontSize: '11px',
              height: '28px',
              textTransform: 'none',
            }}>
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{
              borderColor: '#cbd5e1',
              color: '#475569',
              fontSize: '11px',
              height: '28px',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  const isEditable = item.isEditable !== false && !disabled;

  return (
    <Box
      onClick={isEditable ? onStartEdit : undefined}
      sx={{
        '&:hover': isEditable
          ? {
              '& .edit-icon-btn': { opacity: 1 },
              backgroundColor: '#f8fafc',
            }
          : {},
        borderRadius: '8px',
        cursor: isEditable ? 'pointer' : 'default',
        p: 1.5,
        transition: 'all 0.15s',
      }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={3.5}>
          <Typography
            sx={{
              color: '#475569',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              pt: '2px',
              textTransform: 'uppercase',
            }}>
            {item.label}
            {(item.label === 'Q' || item.label === 'JOB TITLE KEYWORD') && (
              <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>
            )}
          </Typography>
        </Grid>
        <Grid item xs={7.5}>
          <Typography
            sx={{
              color: '#1e293b',
              fontFamily: 'Inter, sans-serif',
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: 1.6,
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {displayValue || '—'}
          </Typography>
        </Grid>
        <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {isEditable && (
            <IconButton
              className="edit-icon-btn"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit();
              }}
              sx={{
                '&:hover': { backgroundColor: '#e2e8f0', color: '#1e293b' },
                color: '#94a3b8',
                opacity: 0,
                padding: '2px',
                transition: 'opacity 0.15s',
              }}>
              <EditIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// ── Loader stages ──────────────────────────────────────────────────────────
type LoaderStage = 1 | 2 | 3;

// ── Main component ─────────────────────────────────────────────────────────

const JobBoardSearch: React.FC<JobBoardSearchProps> = ({
  jobId,
  jobInfo,
  phase,
  results,
  booleanJsonData,
  originalBooleanJsonData,
  onPhaseChange,
  onResultsChange,
  onBooleanJsonDataChange,
  onOriginalBooleanJsonDataChange,
  onOpenCandidateModal,
  score,
  isScoreModified,
  onClose,
  localScore,
  onScoreChange,
}) => {
  const queryClient = useQueryClient();
  const [loaderStage, setLoaderStage] = useState<LoaderStage>(1);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [detailModalCandidate, setDetailModalCandidate] = useState<any | null>(
    null,
  );
  const [viewerFileId, setViewerFileId] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);

  const [shortlistModalOpen, setShortlistModalOpen] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [editingFieldPath, setEditingFieldPath] = useState<string[] | null>(
    null,
  );
  const [isFetchingBoolean, setIsFetchingBoolean] = useState(false);
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [isGeneratingScore, setIsGeneratingScore] = useState(false);
  // scoreGenerated is true if the API says jobBoardScoreInitiated=true OR if user just clicked Generate Score
  const [scoreGenerated, setScoreGenerated] = useState<boolean>(
    jobInfo?.jobBoardScoreInitiated ?? false,
  );
  const [hasIdentityRelevant, setHasIdentityRelevant] = useState(false);
  const isFirstRender = React.useRef(true);

  const [scoreInput, setScoreInput] = useState(localScore || '');

  React.useEffect(() => {
    setScoreInput(localScore || '');
  }, [localScore]);

  const debouncedScoreChange = React.useMemo(
    () =>
      _.debounce((val: string) => {
        onScoreChange?.(val);
      }, 500),
    [onScoreChange],
  );

  // Track attempts from the API response (0 = not yet launched — hide indicator)
  const [searchAttempts, setSearchAttempts] = useState<number>(
    jobInfo?.jobBoardSearchAttempts ?? 0,
  );

  // Fetch the max launch limit from job-config
  const { data: jobConfigData } = useFetchList<any>(
    ['job-config'],
    MASTER_ENDPOINT.JobConfig,
    { refetchOnMount: false },
  );
  const maxAttempts: number =
    jobConfigData?.jobBoardSearchLimit ??
    jobConfigData?.data?.jobBoardSearchLimit ??
    10;

  const attemptsLeft = Math.max(0, maxAttempts - searchAttempts);
  const attemptsExhausted = searchAttempts >= maxAttempts;
  const launchDisabled =
    attemptsExhausted ||
    scoreGenerated ||
    isFetchingBoolean ||
    isSubmittingSearch;

  // ── Fetch job board results ────────────────────────────────────────────

  const queryParams = new URLSearchParams();
  if (isScoreModified && score && score !== '0') {
    queryParams.append('score', String(score));
  }
  queryParams.append('sortby', 'location');
  if (hasIdentityRelevant) {
    queryParams.append('relatedTitle', 'true');
  }
  const getEndpoint = `${String(
    SALES_ENDPOINT.JobBoardResults,
  )}/${jobId}?${queryParams.toString()}`;

  const {
    data: jobBoardData,
    isFetching,
    refetch,
  } = useFetchList<any>(
    ['job-board-results', jobId, score, getEndpoint],
    getEndpoint,
    { enabled: !!jobId },
  );

  const isPayloadModified = React.useMemo(() => {
    if (!booleanJsonData) return false;
    const targetPayload =
      jobBoardData?.actualPayload ??
      jobBoardData?.data?.actualPayload ??
      originalBooleanJsonData;
    if (!targetPayload) return false;

    const clean = (p: any) => {
      if (!p) return null;
      let obj = p;
      if (typeof p === 'string') {
        try {
          obj = JSON.parse(p);
        } catch (_) {
          return p;
        }
      }
      const res = { ...obj };
      delete res.message;
      delete res.status;
      delete res.isModified;
      return res;
    };

    return !_.isEqual(clean(booleanJsonData), clean(targetPayload));
  }, [booleanJsonData, originalBooleanJsonData, jobBoardData]);

  const handleResetPayload = () => {
    setIsFetchingBoolean(true);
    const resetEndpoint = `${String(
      SALES_ENDPOINT.JobBoardResetBoolean,
    )}/${String(jobId)}`;
    triggerResetBoolean(
      { data: {}, endpoint: resetEndpoint },
      {
        onError: (err: any) => {
          setIsFetchingBoolean(false);
          const errorMessage =
            typeof err === 'string'
              ? err
              : err?.response?.data?.message ||
                err?.message ||
                'Failed to reset parameters. Please try again.';
          toast.error(errorMessage);
        },
        onSuccess: (res: any) => {
          setIsFetchingBoolean(false);
          const responseData = res?.data ?? res;
          let parsedData: any;
          if (typeof responseData === 'string') {
            try {
              parsedData = JSON.parse(responseData);
            } catch {
              parsedData = { raw: responseData };
            }
          } else {
            parsedData = responseData;
          }
          const actualPayload = parsedData?.payload ?? parsedData;
          onOriginalBooleanJsonDataChange(actualPayload);
          onBooleanJsonDataChange(actualPayload);
          toast.success('Search parameters reset to original configuration.');
        },
      },
    );
  };

  const showResetButton =
    isPayloadModified && !scoreGenerated && !attemptsExhausted;

  const initialFiltersRef = React.useRef({ hasIdentityRelevant, score });
  const filtersChanged =
    initialFiltersRef.current.score !== score ||
    initialFiltersRef.current.hasIdentityRelevant !== hasIdentityRelevant;

  React.useEffect(() => {
    if (jobBoardData) {
      const data = Array.isArray(jobBoardData?.data)
        ? jobBoardData.data
        : jobBoardData?.data?.data || [];
      if (filtersChanged || results.length === 0 || data.length > 0) {
        onResultsChange(data);
      }
      // Read attempts from response
      const attempts =
        jobBoardData?.jobBoardSearchAttempts ??
        jobBoardData?.data?.jobBoardSearchAttempts ??
        null;
      if (attempts !== null) {
        setSearchAttempts(Number(attempts));
      }

      // Read jobBoardScoreInitiated — if true, lock Generate Score permanently
      const scoreInitiated =
        jobBoardData?.jobBoardScoreInitiated ??
        jobBoardData?.data?.jobBoardScoreInitiated ??
        null;
      if (scoreInitiated === true) {
        setScoreGenerated(true);
      }

      // ── Restore payload after refresh / job-switch ──────────────────
      // booleanJsonData lives in React state and resets to null on navigation.
      // Reconstruct it from whatever the search-raw response echoes back so
      // the left panel always shows editable fields when results exist.
      if (!booleanJsonData) {
        // 1. Try the full payload object the boolean endpoint originally returned
        const fullPayload =
          jobBoardData?.payload ?? jobBoardData?.data?.payload ?? null;

        if (fullPayload) {
          onBooleanJsonDataChange(fullPayload);
          onOriginalBooleanJsonDataChange(fullPayload);
          return;
        }

        // 2. Fallback — reconstruct a minimal wrapper around searchParameters
        const sp =
          jobBoardData?.searchParameters ??
          jobBoardData?.data?.searchParameters ??
          jobInfo?.searchParameters ??
          jobInfo?.payload?.searchParameters ??
          null;

        if (sp) {
          let parsedSp = sp;
          if (typeof sp === 'string') {
            try {
              parsedSp = JSON.parse(sp);
            } catch (_) {
              /* keep as-is */
            }
          }
          // Unwrap nested searchParameters key if present
          if (
            parsedSp?.searchParameters &&
            typeof parsedSp.searchParameters === 'object'
          ) {
            parsedSp = parsedSp.searchParameters;
          }
          const reconstructed = { searchParameters: parsedSp };
          onBooleanJsonDataChange(reconstructed);
          onOriginalBooleanJsonDataChange(reconstructed);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobBoardData, filtersChanged, results.length]);

  React.useEffect(() => {
    if (booleanJsonData) {
      if (!originalBooleanJsonData) {
        onOriginalBooleanJsonDataChange(booleanJsonData);
      }
    } else if (originalBooleanJsonData) {
      onOriginalBooleanJsonDataChange(null);
    }
  }, [
    booleanJsonData,
    originalBooleanJsonData,
    onOriginalBooleanJsonDataChange,
  ]);

  // Fetch overview file IDs for selected candidate
  const matchInfo =
    extractMatchInfo(selectedCandidate) ||
    extractMatchInfo(results.find((c: any) => c.id === selectedCandidate?.id));

  const targetJobMatchId =
    selectedCandidate?.jobMatchCandidateId &&
    selectedCandidate?.jobMatchCandidateId !== selectedCandidate?.id
      ? selectedCandidate.jobMatchCandidateId
      : matchInfo?.jobMatchCandidateId || selectedCandidate?.id;

  const { data: overviewResp, isLoading: isOverviewLoading } =
    useFetchList<any>(
      ['candidate-overview-ids-jb', targetJobMatchId],
      `jobs/${MASTER_ENDPOINT.Candidates}/overview-file/${String(
        targetJobMatchId,
      )}`,
      {
        enabled: !!targetJobMatchId && !!viewerFileId,
        refetchOnMount: 'always',
      },
    );

  const getOverviewData = React.useMemo(
    () => ({
      withGap: overviewResp?.jobOverviewFileId || matchInfo?.jobOverviewFileId,
      withoutGap:
        overviewResp?.jobOverviewWithoutGapsFileId ||
        matchInfo?.jobOverviewWithoutGapsFileId,
    }),
    [overviewResp, matchInfo],
  );

  const candidateIdForAttachments =
    selectedCandidate?.matchedCandidate?.id || selectedCandidate?.id;

  const { data: attachmentsResp } = useFetchList<any>(
    ['candidate-attachments-jb', candidateIdForAttachments],
    `${String(
      MASTER_ENDPOINT.Candidates,
    )}/attachments/files?candidateId=${String(candidateIdForAttachments)}`,
    {
      enabled: !!candidateIdForAttachments && shortlistModalOpen,
      refetchOnMount: 'always',
    },
  );

  const attachments =
    attachmentsResp?.files || attachmentsResp?.data?.files || [];

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void refetch();
  }, [hasIdentityRelevant, refetch]);

  const scoringTimeoutRef = React.useRef<any>(null);

  React.useEffect(() => {
    const unsubscribe = sseService.on('job_scored', (event) => {
      const js = event.data?.job_scored;
      const scoredJobId =
        (typeof js === 'object' && js !== null ? js.jobId || js.id : js) ||
        event.data?.jobId ||
        event.data?.id;

      if (scoredJobId && String(scoredJobId) === String(jobId)) {
        if (scoringTimeoutRef.current) {
          clearTimeout(scoringTimeoutRef.current);
          scoringTimeoutRef.current = null;
        }
        setIsGeneratingScore(false);
        setScoreGenerated(true);
        void refetch();
      }
    });

    return () => {
      unsubscribe();
      if (scoringTimeoutRef.current) {
        clearTimeout(scoringTimeoutRef.current);
      }
    };
  }, [jobId, refetch]);

  React.useEffect(() => {
    const hasTriggered = !!jobInfo?.hasTriggeredJobBoardSearch;
    if (hasTriggered && phase === 'idle') {
      onPhaseChange('results');
    }
  }, [phase, jobInfo?.hasTriggeredJobBoardSearch, onPhaseChange]);

  React.useEffect(() => {
    if (jobInfo) {
      if (jobInfo.jobBoardScoreInitiated !== undefined) {
        setScoreGenerated(jobInfo.jobBoardScoreInitiated);
      }
      if (jobInfo.jobBoardSearchAttempts !== undefined) {
        setSearchAttempts(jobInfo.jobBoardSearchAttempts);
      }
    }
  }, [jobInfo]);

  const { mutate: triggerBoolean, isPending: isPuttingBoolean } =
    useUpdateItem(false);
  const { mutate: triggerResetBoolean } = useUpdateItem(false);
  const { mutate: triggerSearchInitiate } = useUpdateItem(false);
  const { mutate: parseCandidate, isPending: isParsing } = useUpdateItem(false);
  const { mutate: triggerScoreInitiate } = useUpdateItem(false);

  // ── Stage loader ───────────────────────────────────────────────────────

  const stageTimersRef = React.useRef<NodeJS.Timeout[]>([]);
  const clearStageTimers = () => {
    stageTimersRef.current.forEach(clearInterval);
    stageTimersRef.current = [];
  };

  const startLoaderStages = () => {
    clearStageTimers();
    setLoaderStage(1);
    const interval = setInterval(() => {
      setLoaderStage((prev) => (prev >= 3 ? 1 : ((prev + 1) as LoaderStage)));
    }, 2000);
    stageTimersRef.current.push(interval);
  };

  const getLoaderText = () => {
    switch (loaderStage) {
      case 1:
        return {
          desc: 'Analyzing the Job Description to create an optimized search query with required skills and location.',
          title: 'Generating Boolean search parameters...',
        };
      case 2:
        return {
          desc: 'Connecting to Dice and Monster to find the best matching candidates for your requirement.',
          title: 'Searching external job boards...',
        };
      case 3:
        return {
          desc: 'Filtering and ranking candidates based on experience, skills, and visa status.',
          title: 'Processing results...',
        };
      default:
        return { desc: '', title: '' };
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleLaunchSearch = () => {
    if (launchDisabled) return;

    // ── Re-launch (2nd / 3rd attempt) ────────────────────────────────────
    // Payload already exists — go straight to the edit phase and let the user
    // submit directly via search-initiate. No need to call the boolean API again.
    if (booleanJsonData) {
      onPhaseChange('boolean');
      return;
    }

    // ── First launch — call boolean API to generate the payload ───────────
    setIsFetchingBoolean(true);
    const booleanEndpoint = `${String(SALES_ENDPOINT.JobBoardBoolean)}/${String(
      jobId,
    )}`;
    triggerBoolean(
      { data: {}, endpoint: booleanEndpoint },
      {
        onError: (err: any) => {
          setIsFetchingBoolean(false);
          const errorMessage =
            typeof err === 'string'
              ? err
              : err?.response?.data?.message ||
                err?.message ||
                'Failed to generate boolean search. Please try again.';
          toast.error(errorMessage);
        },
        onSuccess: (res: any) => {
          setIsFetchingBoolean(false);
          const responseData = res?.data ?? res;
          let parsedData: any;
          if (typeof responseData === 'string') {
            try {
              parsedData = JSON.parse(responseData);
            } catch {
              parsedData = { raw: responseData };
            }
          } else {
            parsedData = responseData;
          }
          // Read attempts from response
          const attempts =
            parsedData?.jobBoardSearchAttempts ??
            res?.jobBoardSearchAttempts ??
            null;
          if (attempts !== null) {
            setSearchAttempts(Number(attempts));
          }
          onOriginalBooleanJsonDataChange(parsedData);
          onBooleanJsonDataChange(parsedData);
          onPhaseChange('boolean');
        },
      },
    );
  };

  const handleSubmitBooleanSearch = () => {
    setIsSubmittingSearch(true);
    const initiateEndpoint = `${String(
      SALES_ENDPOINT.JobBoardSearchInitiate,
    )}/${String(jobId)}`;

    const getCleanedPayload = (data: any) => {
      if (!data) return null;
      const p = JSON.parse(JSON.stringify(data));
      delete p.message;
      delete p.status;
      delete p.isModified;

      let sp = null;
      if (p.payload?.searchParameters) {
        sp = p.payload.searchParameters;
      } else if (p.searchParameters) {
        sp = p.searchParameters;
      } else {
        sp = p;
      }

      if (sp) {
        if (sp.workPermit === '-' || !sp.workPermit) {
          delete sp.workPermit;
        }
        if (Array.isArray(sp.locations) && sp.locations.length === 0) {
          delete sp.locations;
        }
      }
      return p;
    };

    const payload = getCleanedPayload(booleanJsonData);
    let isModified = false;
    if (originalBooleanJsonData) {
      const cleanOriginal = getCleanedPayload(originalBooleanJsonData);
      isModified = !_.isEqual(payload, cleanOriginal);
    }

    const payloadWithModification = { ...payload, isModified };

    triggerSearchInitiate(
      { data: payloadWithModification, endpoint: initiateEndpoint },
      {
        onError: (err: any) => {
          setIsSubmittingSearch(false);
          const errorMessage =
            typeof err === 'string'
              ? err
              : err?.response?.data?.message ||
                err?.message ||
                'Failed to initiate job board search. Please try again.';
          toast.error(errorMessage);
        },
        onSuccess: (res: any) => {
          setIsSubmittingSearch(false);
          // Update attempts from response
          const attempts =
            res?.jobBoardSearchAttempts ??
            res?.data?.jobBoardSearchAttempts ??
            null;
          if (attempts !== null) {
            setSearchAttempts(Number(attempts));
          } else {
            setSearchAttempts((prev) => prev + 1);
          }

          queryClient.invalidateQueries({
            queryKey: ['job-posting', jobId],
          });

          onPhaseChange('loading');
          startLoaderStages();

          const resolveResults = (res: any) => {
            clearStageTimers();
            const data = Array.isArray(res?.data)
              ? res.data
              : res?.data?.data || [];
            onResultsChange(data);
            onPhaseChange('results');
          };

          const handleError = (err: any) => {
            clearStageTimers();
            onPhaseChange('idle');
            const errorMessage =
              typeof err === 'string'
                ? err
                : err?.response?.data?.message ||
                  err?.message ||
                  'Failed to load job board results. Please try again.';
            toast.error(errorMessage);
          };

          void refetch().then(resolveResults).catch(handleError);
        },
      },
    );
  };

  const handleGenerateScore = () => {
    if (isGeneratingScore || scoreGenerated) return;
    setIsGeneratingScore(true);

    const scoreEndpoint = `${String(
      SALES_ENDPOINT.JobBoardScoreInitiate,
    )}/${String(jobId)}`;

    const getCleanedPayload = (data: any) => {
      if (!data) return null;
      const p = JSON.parse(JSON.stringify(data));
      delete p.message;
      delete p.status;
      delete p.isModified;

      let sp = null;
      if (p.payload?.searchParameters) {
        sp = p.payload.searchParameters;
      } else if (p.searchParameters) {
        sp = p.searchParameters;
      } else {
        sp = p;
      }

      if (sp) {
        if (sp.workPermit === '-' || !sp.workPermit) {
          delete sp.workPermit;
        }
        if (Array.isArray(sp.locations) && sp.locations.length === 0) {
          delete sp.locations;
        }
      }
      return p;
    };

    const payload = getCleanedPayload(booleanJsonData);

    triggerScoreInitiate(
      { data: payload, endpoint: scoreEndpoint },
      {
        onError: (err: any) => {
          setIsGeneratingScore(false);
          const errorMessage =
            typeof err === 'string'
              ? err
              : err?.response?.data?.message ||
                err?.message ||
                'Failed to generate score. Please try again.';
          toast.error(errorMessage);
        },
        onSuccess: () => {
          toast.success(
            'Score generation initiated successfully! Please wait while scores are computed...',
          );
          // Keep isGeneratingScore=true until the SSE job_scored event arrives,
          // which will call setIsGeneratingScore(false) + setScoreGenerated(true)
        },
      },
    );
  };

  const handleShortlistProceed = async (selected: string[]) => {
    if (isShortlisting) return;
    setIsShortlisting(true);

    const matchInfoInProceed =
      extractMatchInfo(selectedCandidate) ||
      extractMatchInfo(
        results.find((c: any) => c.id === selectedCandidate?.id),
      );

    const processedCandidate =
      selectedCandidate?.status === 'processed'
        ? selectedCandidate
        : results.find(
            (c: any) =>
              c.id === selectedCandidate?.id && c.status === 'processed',
          );
    const resumeFileId = processedCandidate
      ? processedCandidate?.resume?.fileId || processedCandidate?.resumeId
      : null;

    const shortlistDocs = [
      ...(selected.includes('Resume')
        ? [
            {
              attachmentId: null,
              documentType: 'resume',
              fileId: resumeFileId || viewerFileId,
              isSelected: true,
            },
          ]
        : []),
      ...(selected.includes('Candidate Overview Summary')
        ? [
            {
              attachmentId: null,
              documentType: 'overviewSummary',
              fileId:
                getOverviewData?.withoutGap ||
                selectedCandidate?.jobOverviewWithoutGapsFileId ||
                matchInfoInProceed?.jobOverviewWithoutGapsFileId,
              isSelected: true,
            },
          ]
        : []),
      ...attachments
        .filter((att) => selected.includes(String(att.id || att.fileId)))
        .map((att) => ({
          attachmentId: att.id,
          documentType: 'attachment',
          fileId: att.fileId,
          isSelected: true,
        })),
    ];

    const payload = {
      candidateId:
        selectedCandidate?.candidate?.id ||
        selectedCandidate?.candidateId ||
        selectedCandidate?.id,
      diceId: selectedCandidate?.id,
      docs: shortlistDocs,
      jobMatchCandidateId:
        selectedCandidate?.jobMatchCandidateId &&
        selectedCandidate?.jobMatchCandidateId !== selectedCandidate?.id
          ? selectedCandidate.jobMatchCandidateId
          : matchInfoInProceed?.jobMatchCandidateId || selectedCandidate?.id,
      sourceQueue: 'jobQueue',
    };

    try {
      await ReplaceData(jobId || '', payload, SALES_ENDPOINT.SHORTLIST, true);
      onResultsChange(
        results.map((c: any) =>
          (c.id || c.fileId) ===
          (selectedCandidate?.id || selectedCandidate?.fileId)
            ? { ...c, isShortlisted: true }
            : c,
        ),
      );
      setSelectedCandidate((prev: any) => ({ ...prev, isShortlisted: true }));
      queryClient.invalidateQueries({
        queryKey: ['all-shortlisted-candidates', jobId],
      });
      queryClient.invalidateQueries({
        queryKey: ['shortlisted-candidates', jobId],
      });
      queryClient.invalidateQueries({ queryKey: ['job-posting', jobId] });
      refetch();
      setShortlistModalOpen(false);
      setViewerFileId(null);
      setDetailModalCandidate(null);
    } catch (error) {
      console.error('Error shortlisting candidate:', error);
      toast.error('Failed to shortlist candidate');
    } finally {
      setIsShortlisting(false);
    }
  };

  // ── Attempt badge ──────────────────────────────────────────────────────

  const AttemptsIndicator = () => {
    // Don't show anything until the user has launched at least once, or if scores are already generated
    if (searchAttempts === 0 || scoreGenerated) return null;
    return (
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: attemptsExhausted
            ? '#fef2f2'
            : attemptsLeft === 1
            ? '#fffbeb'
            : '#f0fdf4',
          border: `1px solid ${
            attemptsExhausted
              ? '#fca5a5'
              : attemptsLeft === 1
              ? '#fde68a'
              : '#86efac'
          }`,
          borderRadius: '20px',
          display: 'flex',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: '3px',
          }}>
          {Array.from({ length: maxAttempts }, (_, i) => i).map((i) => (
            <Box
              key={i}
              sx={{
                backgroundColor:
                  i < searchAttempts
                    ? attemptsExhausted
                      ? '#ef4444'
                      : '#f59e0b'
                    : '#d1fae5',
                border: `1px solid ${
                  i < searchAttempts
                    ? attemptsExhausted
                      ? '#dc2626'
                      : '#d97706'
                    : '#6ee7b7'
                }`,
                borderRadius: '50%',
                height: '8px',
                width: '8px',
              }}
            />
          ))}
        </Box>
        <Typography
          sx={{
            color: attemptsExhausted
              ? '#dc2626'
              : attemptsLeft === 1
              ? '#92400e'
              : '#166534',
            fontSize: '11px',
            fontWeight: 600,
          }}>
          {attemptsExhausted
            ? 'Limit Reached'
            : `${attemptsLeft}/${maxAttempts}`}
        </Typography>
      </Box>
    );
  };
  // ── Payload panel ──────────────────────────────────────────────────────

  const PayloadPanel = () => {
    const fields = booleanJsonData ? getEditableFields(booleanJsonData) : [];

    return (
      <Box
        sx={{
          backgroundColor: '#fafbff',
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          width: '42%',
        }}>
        {/* Left header */}
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            flexShrink: 0,
            gap: 1.5,
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.75,
          }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#1e1b4b',
                borderRadius: '8px',
                display: 'flex',
                height: 32,
                justifyContent: 'center',
                width: 32,
              }}>
              <SearchIcon sx={{ color: '#a5b4fc', fontSize: 16 }} />
            </Box>
            <Box>
              <Typography
                sx={{ color: '#111827', fontSize: '13px', fontWeight: 700 }}>
                Search Parameters
              </Typography>
              <Typography sx={{ color: '#9ca3af', fontSize: '11px' }}>
                Edit fields inline before launching
              </Typography>
            </Box>
          </Box>
          {AttemptsIndicator()}
        </Box>

        {/* Fields area */}
        <Box
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
            },
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1.5,
          }}>
          {phase === 'loading' && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                minHeight: '200px',
                py: 6,
                textAlign: 'center',
              }}>
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '50%',
                  display: 'flex',
                  height: 56,
                  justifyContent: 'center',
                  width: 56,
                }}>
                <SearchIcon sx={{ color: '#9ca3af', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  sx={{ color: '#374151', fontSize: '14px', fontWeight: 600 }}>
                  Searching...
                </Typography>
                <Typography
                  sx={{
                    color: '#9ca3af',
                    fontSize: '12px',
                    mt: 0.5,
                  }}>
                  Payload is being processed
                </Typography>
              </Box>
            </Box>
          )}

          {phase === 'idle' && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 2.5,
                justifyContent: 'center',
                minHeight: '250px',
                px: 3,
                py: 8,
                textAlign: 'center',
              }}>
              <Box
                sx={{
                  alignItems: 'center',
                  color: '#9ca3af',
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 1,
                }}>
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M14 3C7.92487 3 3 7.92487 3 14C3 20.0751 7.92487 25 14 25C20.0751 25 25 20.0751 25 14C25 7.92487 20.0751 3 14 3ZM0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 17.3257 26.8404 20.3805 24.9035 22.7822L35.5607 33.4393C36.1464 34.0251 36.1464 34.9749 35.5607 35.5607C34.9749 36.1464 34.0251 36.1464 33.4393 35.5607L22.7822 24.9035C20.3805 26.8404 17.3257 28 14 28C6.26801 28 0 21.732 0 14ZM14 12C14.8284 12 15.5 12.6716 15.5 13.5V20.5C15.5 21.3284 14.8284 22 14 22C13.1716 22 12.5 21.3284 12.5 20.5V13.5C12.5 12.6716 13.1716 12 14 12ZM14 10C15.1046 10 16 9.10457 16 8C16 6.89543 15.1046 6 14 6C12.8954 6 12 6.89543 12 8C12 9.10457 12.8954 10 14 10Z"
                    fill="currentColor"
                  />
                </svg>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: '#242424',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: 1,
                  }}>
                  Ready to Search External Job Boards
                </Typography>
                <Typography
                  sx={{
                    color: '#8c8c8c',
                    fontSize: '13px',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    margin: '0 auto',
                    maxWidth: '320px',
                  }}>
                  Launch a search on Dice and Monster using auto-generated
                  Boolean logic to find matching candidates.
                </Typography>
              </Box>
            </Box>
          )}

          {(phase === 'boolean' || phase === 'results') &&
            fields.length > 0 &&
            fields.map((item) => {
              const keyStr = item.keyPath.join('.');
              const isEditing =
                editingFieldPath !== null &&
                editingFieldPath.join('.') === keyStr;
              return (
                <BooleanParameterRow
                  key={keyStr}
                  item={item}
                  isEditing={isEditing}
                  onStartEdit={() => setEditingFieldPath(item.keyPath)}
                  onCancel={() => setEditingFieldPath(null)}
                  onSave={(newVal) => {
                    onBooleanJsonDataChange(
                      setDeepValue(booleanJsonData, item.keyPath, newVal),
                    );
                    setEditingFieldPath(null);
                  }}
                  disabled={launchDisabled}
                />
              );
            })}

          {(phase === 'boolean' || phase === 'results') &&
            fields.length === 0 && (
              <Typography sx={{ color: '#9ca3af', fontSize: '12px', py: 2 }}>
                No editable parameters found.
              </Typography>
            )}
        </Box>

        {/* Left footer — Launch button */}
        <Box
          sx={{
            backgroundColor: '#fff',
            borderTop: '1px solid #e5e7eb',
            flexShrink: 0,
            p: 2,
          }}>
          {scoreGenerated && (
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#fef9c3',
                border: '1px solid #fde047',
                borderRadius: '6px',
                display: 'flex',
                gap: 1,
                mb: 1.5,
                p: 1,
              }}>
              <LockOutlinedIcon sx={{ color: '#a16207', fontSize: 14 }} />
              <Typography
                sx={{ color: '#a16207', fontSize: '11px', fontWeight: 600 }}>
                Re-launching is locked after score generation
              </Typography>
            </Box>
          )}
          {attemptsExhausted && !scoreGenerated && (
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                display: 'flex',
                gap: 1,
                mb: 1.5,
                p: 1,
              }}>
              <LockOutlinedIcon sx={{ color: '#dc2626', fontSize: 14 }} />
              <Typography
                sx={{ color: '#dc2626', fontSize: '11px', fontWeight: 600 }}>
                Maximum launch limit ({maxAttempts}) reached
              </Typography>
            </Box>
          )}

          {/* Show Launch / Re-launch or Submit button depending on phase */}
          {phase === 'boolean' ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() =>
                  onPhaseChange(results.length > 0 ? 'results' : 'idle')
                }
                disabled={isSubmittingSearch}
                sx={{
                  borderColor: '#d1d5db',
                  color: '#6b7280',
                  fontSize: '12px',
                  height: '36px',
                  textTransform: 'none',
                }}>
                Cancel
              </Button>
              {showResetButton && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetPayload}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                      borderColor: '#111',
                    },
                    borderColor: '#242424',
                    color: '#242424',
                    fontSize: '12px',
                    height: '36px',
                    px: 3,
                    textTransform: 'none',
                  }}>
                  Reset
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={
                  isSubmittingSearch ? (
                    <CircularProgress size={12} sx={{ color: '#fff' }} />
                  ) : (
                    <RocketLaunchIcon sx={{ fontSize: '14px !important' }} />
                  )
                }
                onClick={handleSubmitBooleanSearch}
                disabled={isSubmittingSearch || !booleanJsonData}
                sx={{
                  '&:disabled': {
                    backgroundColor: '#d1d5db',
                    color: '#9ca3af',
                  },
                  '&:hover': { backgroundColor: '#111', boxShadow: 'none' },
                  backgroundColor: '#1e1b4b',
                  boxShadow: 'none',
                  fontSize: '12px',
                  height: '36px',
                  textTransform: 'none',
                }}>
                {isSubmittingSearch ? 'Launching...' : 'Launch Search'}
              </Button>
            </Box>
          ) : (
            <Tooltip
              title={
                scoreGenerated
                  ? 'Re-launching disabled after score generation'
                  : attemptsExhausted
                  ? `Maximum ${maxAttempts} launches reached`
                  : ''
              }
              placement="top">
              <span style={{ display: 'block' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {showResetButton && (
                    <Button
                      variant="outlined"
                      onClick={handleResetPayload}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f3f4f6',
                          borderColor: '#111',
                        },
                        borderColor: '#242424',
                        color: '#242424',
                        fontSize: '13px',
                        height: '40px',
                        px: 3,
                        textTransform: 'none',
                      }}>
                      Reset
                    </Button>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      isFetchingBoolean || isPuttingBoolean ? (
                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                      ) : launchDisabled ? (
                        <LockOutlinedIcon
                          sx={{ fontSize: '16px !important' }}
                        />
                      ) : phase === 'idle' ? (
                        <SearchIcon sx={{ fontSize: '16px !important' }} />
                      ) : (
                        <RocketLaunchIcon
                          sx={{ fontSize: '16px !important' }}
                        />
                      )
                    }
                    onClick={handleLaunchSearch}
                    disabled={launchDisabled}
                    sx={{
                      '&:disabled': {
                        backgroundColor: '#e5e7eb',
                        color: '#9ca3af',
                      },
                      '&:hover': {
                        backgroundColor: '#1e1b4b',
                        boxShadow: 'none',
                      },
                      backgroundColor: launchDisabled ? '#e5e7eb' : '#242424',
                      boxShadow: 'none',
                      fontSize: '13px',
                      height: '40px',
                      textTransform: 'none',
                    }}>
                    {isFetchingBoolean || isPuttingBoolean
                      ? 'Generating...'
                      : phase === 'results'
                      ? '↩ Re-launch Search'
                      : 'View Search Parameters'}
                  </Button>
                </Box>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  // ── Results panel ──────────────────────────────────────────────────────

  const ResultsPanel = () => {
    if (phase === 'loading') {
      const text = getLoaderText();
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#f0f4ff',
              borderRadius: '50%',
              display: 'flex',
              height: 72,
              justifyContent: 'center',
              mb: 3,
              width: 72,
            }}>
            <CircularProgress
              size={36}
              sx={{ color: '#4f46e5' }}
              thickness={3}
            />
          </Box>
          <Typography
            sx={{ color: '#111827', fontSize: '16px', fontWeight: 700, mb: 1 }}>
            {text.title}
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: '13px',
              lineHeight: 1.6,
              maxWidth: '320px',
            }}>
            {text.desc}
          </Typography>
          <Box sx={{ mt: 3, width: '200px' }}>
            <LinearProgress
              sx={{
                '& .MuiLinearProgress-bar': { backgroundColor: '#4f46e5' },
                backgroundColor: '#e0e7ff',
                borderRadius: '4px',
                height: '4px',
              }}
            />
          </Box>
        </Box>
      );
    }

    if (phase === 'idle' || phase === 'boolean') {
      return (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '50%',
              display: 'flex',
              height: 72,
              justifyContent: 'center',
              mb: 3,
              width: 72,
            }}>
            <StarIcon sx={{ color: '#d1d5db', fontSize: 36 }} />
          </Box>
          <Typography
            sx={{ color: '#374151', fontSize: '16px', fontWeight: 700, mb: 1 }}>
            Candidates will appear here
          </Typography>
          <Typography
            sx={{
              color: '#9ca3af',
              fontSize: '13px',
              lineHeight: 1.6,
              maxWidth: '280px',
            }}>
            {phase === 'boolean'
              ? 'Review and edit the search parameters, then click Launch Search'
              : 'Click Launch Search to find matching candidates from external job boards'}
          </Typography>
        </Box>
      );
    }

    // Results phase
    return (
      <>
        {/* Results header */}
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
          }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography
              sx={{ color: '#111827', fontSize: '14px', fontWeight: 700 }}>
              Search Results
            </Typography>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#f0f4ff',
                border: '1px solid #c7d7fe',
                borderRadius: '20px',
                display: 'flex',
                height: 22,
                justifyContent: 'center',
                minWidth: 40,
                px: 1,
              }}>
              <Typography
                sx={{ color: '#4338ca', fontSize: '11px', fontWeight: 700 }}>
                {results.length}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasIdentityRelevant}
                  disabled={isFetching}
                  onChange={(e) => setHasIdentityRelevant(e.target.checked)}
                  sx={{
                    '&.Mui-checked': { color: '#4f46e5' },
                    color: '#9ca3af',
                    padding: '4px',
                  }}
                />
              }
              label="Matching Titles"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: '#374151',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '12px',
                  fontWeight: 500,
                },
                margin: 0,
              }}
            />
            {scoreGenerated && (
              <>
                <Box
                  onClick={() => onOpenCandidateModal?.()}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}>
                  View All Scores →
                </Box>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                  <TextField
                    value={scoreInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setScoreInput(val);
                      debouncedScoreChange(val);
                    }}
                    placeholder="0"
                    size="small"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#dfe1e6 !important',
                        },
                        '& input': {
                          borderRight: '1px solid #dfe1e6 !important',
                          color: '#172b4d',
                          fontWeight: 600,
                          height: '30px !important',
                          padding: '4px 4px !important',
                          textAlign: 'center',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4c9aff !important',
                          borderWidth: '1px !important',
                        },
                        '&:hover fieldset': {
                          borderColor: '#c1c7d0 !important',
                        },
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '12px',
                        height: '32px',
                        paddingRight: '5px',
                      },
                      width: '65px',
                    }}
                    InputProps={{
                      endAdornment: (
                        <Box
                          component="span"
                          sx={{
                            color: '#5E6C84',
                            fontSize: '12px',
                            fontWeight: 600,
                            pl: 0.5,
                            pointerEvents: 'none',
                          }}>
                          %
                        </Box>
                      ),
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        {isFetching && (
          <LinearProgress
            sx={{
              '& .MuiLinearProgress-bar': { backgroundColor: '#4f46e5' },
              backgroundColor: '#e0e7ff',
              flexShrink: 0,
              height: '2px',
            }}
          />
        )}

        {/* Candidate list */}
        <Box
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
            },
            flex: 1,
            overflowY: 'auto',
            p: 2,
          }}>
          {results.map((candidate: any) => {
            const fullName =
              toTitleCase(
                [candidate.firstName, candidate.lastName]
                  .filter(Boolean)
                  .join(' '),
              ) || '—';

            const visaBadge =
              candidate.workPermitDocuments?.[0] ??
              candidate.visaStatus ??
              candidate.visa ??
              null;

            const locationParts = [candidate.city, candidate.region].filter(
              Boolean,
            );
            const location = locationParts.join(', ');

            const lastActiveRaw =
              candidate.dateLastActive || candidate.ceipalModifiedAt;
            const lastActive = formatDate(lastActiveRaw);

            const contactParts = [candidate.currentJobTitle].filter(Boolean);
            const contactLine = contactParts.join(' • ');

            const rawSkills =
              candidate.skills ||
              candidate.topSkills ||
              candidate.skillSet ||
              [];
            const topSkills = (Array.isArray(rawSkills) ? rawSkills : [])
              .slice(0, 8)
              .map((s: any) => {
                if (typeof s === 'string') return toTitleCase(s);
                return toTitleCase(s.skill ?? s.name ?? s.label ?? '');
              })
              .filter(Boolean);

            const cardId = candidate.id ?? candidate.fileId;
            const isExpanded = expandedCardId === cardId;

            const statusLabel =
              candidate.status === 'readyForReview'
                ? 'Ready for Review'
                : toTitleCase(candidate.status ?? 'Pending');

            return (
              <Box
                key={cardId}
                sx={{
                  '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  mb: 1.5,
                  overflow: 'hidden',
                  transition: 'box-shadow 0.2s',
                }}>
                {/* Card main row */}
                <Box
                  sx={{
                    alignItems: 'center',
                    cursor:
                      candidate?.status === 'scored' ||
                      candidate?.status === 'processed'
                        ? 'pointer'
                        : 'default',
                    display: 'flex',
                    gap: 1.5,
                    justifyContent: 'space-between',
                    p: 1.5,
                  }}
                  onClick={() => {
                    if (candidate?.status === 'scored') {
                      setDetailModalCandidate(candidate);
                    } else if (candidate?.status === 'processed') {
                      const candidateId =
                        candidate.candidate?.id ||
                        candidate.candidates?.[0]?.id ||
                        candidate.candidateId;
                      if (candidateId) {
                        const matchInfo = extractMatchInfo(candidate);
                        setSelectedCandidate({
                          ...candidate,
                          candidate:
                            candidate.candidate || candidate.candidates?.[0],
                          candidateId: candidateId,
                          id: candidate.id,
                          jobMatchCandidateId:
                            matchInfo?.jobMatchCandidateId || candidate.id,
                          jobOverviewFileId: matchInfo?.jobOverviewFileId,
                          jobOverviewWithoutGapsFileId:
                            matchInfo?.jobOverviewWithoutGapsFileId,
                        });
                        setViewerFileId(candidateId);
                      } else {
                        toast.error(
                          'No candidate ID found for this processed candidate',
                        );
                      }
                    }
                  }}>
                  {/* Candidate info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Badges */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mb: 0.75,
                      }}>
                      {candidate.matchPercentage && (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenCandidateModal) {
                              onOpenCandidateModal(String(candidate.id));
                            }
                          }}
                          sx={{
                            '&:hover': onOpenCandidateModal
                              ? { opacity: 0.8 }
                              : {},
                            backgroundColor: '#4f46e5',
                            borderRadius: '4px',
                            color: '#fff',
                            cursor: onOpenCandidateModal
                              ? 'pointer'
                              : 'default',
                            fontSize: '10px',
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.25,
                          }}>
                          {candidate.matchPercentage}%
                        </Box>
                      )}
                      {visaBadge && (
                        <Box
                          sx={{
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: '4px',
                            color: '#166534',
                            fontSize: '10px',
                            fontWeight: 500,
                            px: 0.75,
                            py: 0.25,
                          }}>
                          {visaBadge}
                        </Box>
                      )}
                      {location && (
                        <Box
                          sx={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '4px',
                            color: '#475569',
                            fontSize: '10px',
                            fontWeight: 500,
                            px: 0.75,
                            py: 0.25,
                          }}>
                          📍 {location}
                        </Box>
                      )}
                      {lastActive && (
                        <Box
                          sx={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '4px',
                            color: '#92400e',
                            fontSize: '10px',
                            fontWeight: 500,
                            px: 0.75,
                            py: 0.25,
                          }}>
                          Active: {lastActive}
                        </Box>
                      )}
                    </Box>
                    {/* Name */}
                    <Typography
                      sx={{
                        color: '#111827',
                        fontSize: '13px',
                        fontWeight: 700,
                        mb: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {fullName}
                    </Typography>
                    {contactLine && (
                      <Typography
                        sx={{
                          color: '#6b7280',
                          fontSize: '11px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {contactLine}
                      </Typography>
                    )}
                  </Box>

                  {/* Right: status + expand */}
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexShrink: 0,
                      gap: 0.5,
                    }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (candidate?.status === 'scored') {
                          setDetailModalCandidate(candidate);
                        } else if (candidate?.status === 'processed') {
                          const candidateId =
                            candidate.candidate?.id ||
                            candidate.candidates?.[0]?.id ||
                            candidate.candidateId;
                          if (candidateId) {
                            const matchInfo = extractMatchInfo(candidate);
                            setSelectedCandidate({
                              ...candidate,
                              candidate:
                                candidate.candidate ||
                                candidate.candidates?.[0],
                              candidateId: candidateId,
                              id: candidate.id,
                              jobMatchCandidateId:
                                matchInfo?.jobMatchCandidateId || candidate.id,
                              jobOverviewFileId: matchInfo?.jobOverviewFileId,
                              jobOverviewWithoutGapsFileId:
                                matchInfo?.jobOverviewWithoutGapsFileId,
                            });
                            setViewerFileId(candidateId);
                          } else {
                            toast.error(
                              'No candidate ID found for this processed candidate',
                            );
                          }
                        }
                      }}
                      sx={{
                        borderColor: '#e5e7eb',
                        borderRadius: '6px',
                        color: '#374151',
                        fontSize: '10px',
                        fontWeight: 600,
                        height: '26px',
                        px: 1,
                        textTransform: 'none',
                      }}>
                      {statusLabel}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCardId(isExpanded ? null : cardId);
                      }}
                      sx={{ color: '#9ca3af', padding: '4px' }}>
                      {isExpanded ? (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none">
                          <path
                            d="M18 15L12 9L6 15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none">
                          <path
                            d="M6 9L12 15L18 9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </IconButton>
                  </Box>
                </Box>

                {/* Expanded skills */}
                {isExpanded && topSkills.length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: '#f9fafb',
                      borderTop: '1px solid #f3f4f6',
                      p: 1.5,
                    }}>
                    <Typography
                      sx={{
                        color: '#9ca3af',
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        mb: 1,
                        textTransform: 'uppercase',
                      }}>
                      Top Skills
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {topSkills.map((skill: string, i: number) => (
                        <Box
                          key={i}
                          sx={{
                            backgroundColor: '#e0e7ff',
                            borderRadius: '4px',
                            color: '#3730a3',
                            fontSize: '11px',
                            fontWeight: 500,
                            px: 0.75,
                            py: 0.25,
                          }}>
                          {skill}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}

          {results.length === 0 && !isFetching && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
              }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '13px' }}>
                No candidates found. Try adjusting your search parameters and
                re-launching.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Generate Score footer */}
        <Box
          sx={{
            backgroundColor: '#fff',
            borderTop: '1px solid #e5e7eb',
            flexShrink: 0,
            p: 2,
          }}>
          {scoreGenerated && (
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#f0fdf4',
                border: '1px solid #86efac',
                borderRadius: '6px',
                display: 'flex',
                gap: 1,
                mb: 1.5,
                p: 1,
              }}>
              <Typography sx={{ fontSize: '13px' }}>✅</Typography>
              <Typography
                sx={{ color: '#166534', fontSize: '11px', fontWeight: 600 }}>
                Score generation has been initiated successfully!
              </Typography>
            </Box>
          )}
          <Tooltip
            title={
              scoreGenerated
                ? 'Score has already been generated for this job'
                : results.length === 0
                ? 'No candidates to score'
                : ''
            }
            placement="top">
            <span style={{ display: 'block' }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={
                  isGeneratingScore ? null : (
                    <ScoreboardIcon sx={{ fontSize: '16px !important' }} />
                  )
                }
                onClick={handleGenerateScore}
                disabled={
                  isGeneratingScore || scoreGenerated || results.length === 0
                }
                sx={{
                  '&:disabled': {
                    backgroundColor: scoreGenerated ? '#d1fae5' : '#e5e7eb',
                    color: scoreGenerated ? '#166534' : '#9ca3af',
                  },
                  '&:hover': { backgroundColor: '#4338ca', boxShadow: 'none' },
                  backgroundColor: '#4f46e5',
                  boxShadow: 'none',
                  fontSize: '13px',
                  height: '40px',
                  textTransform: 'none',
                }}>
                {isGeneratingScore
                  ? 'Initiating Score...'
                  : scoreGenerated
                  ? '✓ Score Generated'
                  : '⭐ Generate Score'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </>
    );
  };

  // ── Full modal layout ──────────────────────────────────────────────────

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}>
      {/* Modal header */}
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          flexShrink: 0,
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
        }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: '#1e1b4b',
              borderRadius: '8px',
              display: 'flex',
              height: 32,
              justifyContent: 'center',
              width: 32,
            }}>
            <SearchIcon sx={{ color: '#a5b4fc', fontSize: 16 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: '#111827', fontSize: '15px', fontWeight: 700 }}>
              Job Board Search
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '11px' }}>
              {jobInfo?.jobTitle || 'External candidate search'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            '&:hover': { backgroundColor: '#f3f4f6' },
            borderRadius: '8px',
            color: '#374151',
            padding: '8px',
          }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Split panel body */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* LEFT: Payload */}
        {PayloadPanel()}

        {/* RIGHT: Results */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          {ResultsPanel()}
        </Box>
      </Box>

      {/* ── Candidate Detail Modal ────────────────────────────── */}
      <Modal
        open={!!detailModalCandidate}
        onClose={() => setDetailModalCandidate(null)}
        className="jb-candidate-modal">
        <Box className="jb-modal-content">
          <Box className="jb-modal-header">
            <Box>
              <Typography className="candidate-name-title">
                {detailModalCandidate
                  ? toTitleCase(
                      [
                        detailModalCandidate.firstName,
                        detailModalCandidate.lastName,
                      ]
                        .filter(Boolean)
                        .join(' '),
                    )
                  : ''}
              </Typography>
              <Typography className="candidate-job-company">
                {detailModalCandidate?.currentJobTitle}{' '}
                {detailModalCandidate?.currentCompany ? '@' : ''}{' '}
                {detailModalCandidate?.currentCompany}
              </Typography>
            </Box>
            <IconButton onClick={() => setDetailModalCandidate(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className="jb-modal-body">
            <Box className="detail-section">
              <Typography className="section-title">Highlights</Typography>
              <Grid container spacing={3} className="highlights-grid">
                <Grid item xs={4}>
                  <Typography className="grid-label">
                    Year of experience
                  </Typography>
                  <Typography className="grid-value">
                    {detailModalCandidate?.yearsOfExperience || '0'} Years
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className="grid-label">
                    Desired Work Type
                  </Typography>
                  <Typography className="grid-value">
                    {detailModalCandidate?.workPreferences?.employmentType?.join(
                      ', ',
                    ) || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className="grid-label">Location</Typography>
                  <Typography className="grid-value">
                    {[detailModalCandidate?.city, detailModalCandidate?.region]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className="grid-label">
                    Likely to Switch
                  </Typography>
                  <Typography className="grid-value">
                    {detailModalCandidate?.likelyToMove ? 'Likely' : 'Unlikely'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className="grid-label">
                    Desired Work Setting
                  </Typography>
                  <Typography className="grid-value">
                    {detailModalCandidate?.workPreferences?.willingToRelocate
                      ? 'Willing to Relocate'
                      : 'On-site'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography className="grid-label">
                    Work Authorization
                  </Typography>
                  <Typography className="grid-value">
                    {detailModalCandidate?.workPermitDocuments?.[0] || '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider className="modal-divider" />

            <Box className="detail-section">
              <Typography className="section-title">Skills</Typography>
              <Box className="skills-grid">
                {(detailModalCandidate?.skills || []).map(
                  (skill: any, idx: number) => (
                    <Box key={idx} className="skill-chip-card">
                      <Typography className="skill-name">
                        {toTitleCase(skill.skill)}
                      </Typography>
                      <Box className="skill-stats">
                        <Typography className="stat-text">
                          {skill.yearsUsed}yrs
                        </Typography>
                        <Typography className="stat-text">
                          {skill.lastUsed}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          </Box>

          <Box className="jb-modal-footer">
            <Button
              className="cancel-btn"
              onClick={() => setDetailModalCandidate(null)}>
              Cancel
            </Button>
            <Button
              className="proceed-btn"
              variant="contained"
              disabled={isParsing}
              onClick={() => {
                if (detailModalCandidate.status === 'processed') {
                  const candidateId =
                    detailModalCandidate.candidate?.id ||
                    detailModalCandidate.candidates?.[0]?.id ||
                    detailModalCandidate.candidateId;
                  if (candidateId) {
                    const matchInfo = extractMatchInfo(detailModalCandidate);
                    setSelectedCandidate({
                      ...detailModalCandidate,
                      candidate:
                        detailModalCandidate.candidate ||
                        detailModalCandidate.candidates?.[0],
                      candidateId: candidateId,
                      id: detailModalCandidate.id,
                      jobMatchCandidateId:
                        matchInfo?.jobMatchCandidateId ||
                        detailModalCandidate.id,
                      jobOverviewFileId: matchInfo?.jobOverviewFileId,
                      jobOverviewWithoutGapsFileId:
                        matchInfo?.jobOverviewWithoutGapsFileId,
                    });
                    setViewerFileId(candidateId);
                    setDetailModalCandidate(null);
                  } else {
                    toast.error(
                      'No candidate ID found for this processed candidate',
                    );
                  }
                  return;
                }
                const parseEndpoint = `${String(
                  SALES_ENDPOINT.JobBoard,
                ).replace('/search', '')}/parsing/${String(jobId)}/${String(
                  detailModalCandidate.id,
                )}`;
                parseCandidate(
                  { data: {}, endpoint: parseEndpoint },
                  {
                    onError: (err: any) => {
                      const errorMessage =
                        typeof err === 'string'
                          ? err
                          : err?.response?.data?.message ||
                            err?.message ||
                            'Failed to parse candidate. Please try again.';
                      toast.error(errorMessage);
                    },
                    onSuccess: (res: any) => {
                      toast.success('Candidate parsing initiated successfully');
                      setDetailModalCandidate(null);
                      const fileId = res?.data?.fileId || res?.fileId;
                      if (fileId) {
                        setSelectedCandidate({
                          ...detailModalCandidate,
                          candidate: res?.data?.candidate || res?.candidate,
                          candidateId:
                            res?.data?.candidateId ||
                            res?.candidateId ||
                            res?.data?.candidate?.id ||
                            res?.candidate?.id,
                          id:
                            res?.data?.id || res?.id || detailModalCandidate.id,
                          jobMatchCandidateId:
                            res?.data?.jobMatchCandidateId ||
                            res?.jobMatchCandidateId,
                          jobOverviewFileId:
                            res?.data?.jobOverviewFileId ||
                            res?.jobOverviewFileId,
                          jobOverviewWithoutGapsFileId:
                            res?.data?.jobOverviewWithoutGapsFileId ||
                            res?.jobOverviewWithoutGapsFileId,
                        });
                        setViewerFileId(fileId);
                      }
                      void refetch().then((res: any) => {
                        const data = Array.isArray(res?.data)
                          ? res.data
                          : res?.data?.data || [];
                        onResultsChange(data);
                      });
                    },
                  },
                );
              }}>
              {isParsing ? 'Processing...' : 'Select to Proceed'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Resume Viewer Modal ───────────────────────────────── */}
      <Modal
        open={!!viewerFileId}
        onClose={() => setViewerFileId(null)}
        className="jb-resume-viewer-modal">
        <Box className="jb-viewer-content">
          <Box className="jb-viewer-header">
            <Typography className="viewer-title">Resume Viewer</Typography>
            <IconButton onClick={() => setViewerFileId(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className="jb-viewer-body">
            {viewerFileId && (
              <Box className="jb-pdf-wrapper">
                <JDHighlightPDFViewer
                  fileUrl={`${process.env.REACT_APP_APIURL}${
                    MASTER_ENDPOINT.Files
                  }/${viewerFileId}?type=${
                    selectedCandidate?.status === 'processed' ? 'resume' : 'pdf'
                  }`}
                  jobInfo={jobInfo}
                  booleanJsonData={booleanJsonData}
                />
              </Box>
            )}
          </Box>
          {!selectedCandidate?.isShortlisted && (
            <Box className="jb-viewer-footer">
              <Button
                className="shortlist-btn"
                variant="contained"
                disabled={isShortlisting || isOverviewLoading}
                onClick={() => setShortlistModalOpen(true)}>
                {isShortlisting ? 'Shortlisting...' : 'Shortlist Candidate'}
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      <ShortlistModal
        open={shortlistModalOpen}
        onClose={() => setShortlistModalOpen(false)}
        attachments={attachments}
        isSubmitting={isShortlisting}
        onProceed={handleShortlistProceed}
      />
    </Box>
  );
};

export default JobBoardSearch;
